<script setup lang="ts">
// One question of a FAQ as a sheet of card stock: a native <details> (works without
// JS, and the answer is in the static HTML for crawlers). The "+" is a small cream
// tag that turns into "×" when open. Used by /faq and the landing's FAQ teaser.
withDefaults(
  defineProps<{
    question: string;
    answer: string;
    /** Heading level of the question (h2 on /faq, h3 inside a landing section). */
    heading?: 'h2' | 'h3';
    /** Slight hand-placed tilt, degrees. */
    tilt?: number;
  }>(),
  { heading: 'h3', tilt: 0 },
);
</script>

<template>
  <details
    class="faq-sheet paper-sheet group"
    :style="{ '--tilt': `${tilt}deg` }"
  >
    <summary class="flex cursor-pointer list-none items-center justify-between gap-5 px-5 py-4 md:px-7 md:py-5">
      <component
        :is="heading"
        class="text-foreground"
        :class="heading === 'h2' ? 'type-subheading' : 'type-app-heading'"
      >
        {{ question }}
      </component>
      <span
        class="faq-plus paper-tag grid h-7 w-7 shrink-0 place-items-center text-lg leading-none"
        aria-hidden="true"
      >+</span>
    </summary>
    <div class="px-5 pb-5 text-[15.5px] leading-[1.6] text-muted-foreground md:px-7 md:pb-6">
      {{ answer }}
    </div>
  </details>
</template>

<style scoped>
.faq-sheet {
  transform: rotate(var(--tilt));
}
.faq-sheet > summary::-webkit-details-marker {
  display: none;
}
.faq-sheet > summary {
  border-radius: var(--paper-radius);
}
.faq-sheet > summary:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 2px;
}
/* The toggle: a small hand-cut cream disc; its "+" turns into "×" when open. */
.faq-plus {
  border-radius: 52% 48% 50% 46% / 48% 52% 46% 50%;
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 2px 4px -1px var(--paper-shade);
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}
.faq-sheet[open] .faq-plus {
  transform: rotate(45deg);
}
@media (prefers-reduced-motion: reduce) {
  .faq-plus {
    transition: none;
  }
}
</style>
